/**
 * Matching and spelling bee — the last two games `01-information-architecture` promises.
 *
 * Both are built from the learner's own due cards, like the word search and the
 * crossword, and both feed the review scheduler. What distinguishes them is the
 * modality, which is the only reason to have four games rather than one:
 *
 *   - **Word search** — spot a word that is in front of you.            `recognition`
 *   - **Matching**    — pick its meaning from a handful of options.     `recognition`
 *   - **Crossword**   — produce it from its meaning.                    `production`
 *   - **Spelling bee** — produce it letter by letter, from its meaning. `production`
 *
 * A game that graded as the wrong modality would be worse than no game: it would tell
 * the scheduler the learner did something easier or harder than they did, and the error
 * compounds through every subsequent interval.
 */

import { createRng, gridForm } from './wordsearch.js';

// ── Matching ──────────────────────────────────────────────────────────────────

export interface MatchPair {
  readonly itemId: string;
  /** The target-language word. */
  readonly term: string;
  /** Its meaning, which is what the learner matches it to. */
  readonly gloss: string;
}

export interface MatchingBoard {
  /** Terms in the order shown. */
  readonly terms: readonly MatchPair[];
  /** The same pairs, shuffled independently, so position carries no hint. */
  readonly glosses: readonly MatchPair[];
}

/**
 * Builds a board.
 *
 * Both columns are shuffled, and that is the whole mechanic: shuffling only one leaves
 * the answers in their original order against a scrambled left column, which a learner
 * solves by position rather than by meaning on the second round.
 *
 * Capped at eight pairs — sixteen tiles is the most that fits a phone without scrolling,
 * and a matching game you have to scroll is a memory test of the screen rather than of
 * the words.
 */
export function buildMatching(pairs: readonly MatchPair[], seed: string, limit = 8): MatchingBoard {
  const usable = pairs.filter((pair) => pair.term.length > 0 && pair.gloss.length > 0);
  const chosen = shuffle(usable, createRng(`${seed}:choose`)).slice(0, limit);
  return {
    terms: shuffle(chosen, createRng(`${seed}:terms`)),
    glosses: shuffle(chosen, createRng(`${seed}:glosses`)),
  };
}

/** Fisher–Yates with a seeded PRNG, so a board is reproducible. */
function shuffle<T>(items: readonly T[], rng: () => number): readonly T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i];
    const b = out[j];
    if (a !== undefined && b !== undefined) {
      out[i] = b;
      out[j] = a;
    }
  }
  return out;
}

export interface MatchingState {
  readonly matched: readonly string[];
  /** Wrong pairings. Counted, never named as a mistake. */
  readonly misses: number;
  /** The term the learner has picked and not yet paired, if any. */
  readonly pickedTerm: string | null;
  readonly startedAt: number;
}

export function startMatching(now: number): MatchingState {
  return { matched: [], misses: 0, pickedTerm: null, startedAt: now };
}

export interface MatchOutcome {
  readonly state: MatchingState;
  /** True when this tap completed a correct pair. */
  readonly paired: boolean;
}

/**
 * Taps a term, then a gloss.
 *
 * Tapping a second term replaces the first rather than counting a miss: changing your
 * mind before committing is not a wrong answer, and penalising it teaches hesitation.
 */
export function tapTerm(state: MatchingState, itemId: string): MatchingState {
  if (state.matched.includes(itemId)) return state;
  return { ...state, pickedTerm: itemId };
}

export function tapGloss(state: MatchingState, itemId: string): MatchOutcome {
  if (state.pickedTerm === null || state.matched.includes(itemId)) {
    return { state, paired: false };
  }
  if (state.pickedTerm === itemId) {
    return {
      state: { ...state, matched: [...state.matched, itemId], pickedTerm: null },
      paired: true,
    };
  }
  // A miss clears the selection: leaving it picked invites the learner to stab at the
  // same term repeatedly, which is frustration rather than practice.
  return { state: { ...state, misses: state.misses + 1, pickedTerm: null }, paired: false };
}

export function isMatchingComplete(board: MatchingBoard, state: MatchingState): boolean {
  return board.terms.every((pair) => state.matched.includes(pair.itemId));
}

// ── Spelling bee ──────────────────────────────────────────────────────────────

export interface SpellingPrompt {
  readonly itemId: string;
  /** The word to spell, as written — accents and all. */
  readonly answer: string;
  /** What the learner is shown: the meaning. */
  readonly clue: string;
}

export interface SpellingState {
  readonly index: number;
  readonly correct: readonly string[];
  /** Prompts the learner got wrong. Not submitted as lapses; see `spellingGrades`. */
  readonly wrong: readonly string[];
  readonly startedAt: number;
}

export function startSpelling(now: number): SpellingState {
  return { index: 0, correct: [], wrong: [], startedAt: now };
}

/**
 * Whether an attempt spells the word.
 *
 * Compared on the folded form, so a learner who cannot type `ñ` or an accent on their
 * keyboard is not marked wrong for it. The accents matter and are taught elsewhere; a
 * spelling game that fails someone over a keyboard layout is testing the keyboard.
 */
export function spellsCorrectly(attempt: string, answer: string): boolean {
  return gridForm(attempt) === gridForm(answer) && gridForm(answer).length > 0;
}

export interface SpellingOutcome {
  readonly state: SpellingState;
  readonly correct: boolean;
  /** The answer as written, revealed after an attempt either way. */
  readonly answer: string;
}

export function submitSpelling(
  prompts: readonly SpellingPrompt[],
  state: SpellingState,
  attempt: string,
): SpellingOutcome {
  const prompt = prompts[state.index];
  if (prompt === undefined) {
    return { state, correct: false, answer: '' };
  }
  const correct = spellsCorrectly(attempt, prompt.answer);
  return {
    state: {
      ...state,
      index: state.index + 1,
      correct: correct ? [...state.correct, prompt.itemId] : state.correct,
      wrong: correct ? state.wrong : [...state.wrong, prompt.itemId],
    },
    correct,
    answer: prompt.answer,
  };
}

export function isSpellingComplete(
  prompts: readonly SpellingPrompt[],
  state: SpellingState,
): boolean {
  return state.index >= prompts.length;
}

// ── Grading ───────────────────────────────────────────────────────────────────

export interface GameModalityGrade {
  readonly cardId: string;
  readonly modality: 'recognition' | 'production';
  readonly correct: boolean;
  readonly elapsedMs: number;
  readonly hintUsed: false;
  readonly reviewedAt: number;
}

/** Matched pairs, as `recognition`: the meaning was chosen from options on screen. */
export function matchingGrades(
  state: MatchingState,
  cardIdByItemId: Readonly<Record<string, string>>,
  now: number,
): readonly GameModalityGrade[] {
  return gradeAll(state.matched, [], cardIdByItemId, 'recognition', state.startedAt, now);
}

/**
 * Spelling attempts, as `production`.
 *
 * A wrong spelling **is** submitted, as an incorrect review — and this is the one place
 * in the four games where a failure is reported. The difference is that the learner
 * answered: they were asked to spell a word and could not. In the word search and the
 * crossword, an unfound word means they stopped looking, which says nothing about
 * recall and must not be graded.
 */
export function spellingGrades(
  state: SpellingState,
  cardIdByItemId: Readonly<Record<string, string>>,
  now: number,
): readonly GameModalityGrade[] {
  return gradeAll(state.correct, state.wrong, cardIdByItemId, 'production', state.startedAt, now);
}

function gradeAll(
  right: readonly string[],
  wrong: readonly string[],
  cardIdByItemId: Readonly<Record<string, string>>,
  modality: 'recognition' | 'production',
  startedAt: number,
  now: number,
): readonly GameModalityGrade[] {
  const answered = right.length + wrong.length;
  const elapsed = Math.max(1, Math.round((now - startedAt) / Math.max(1, answered)));
  const grades: GameModalityGrade[] = [];
  for (const [itemIds, correct] of [
    [right, true],
    [wrong, false],
  ] as const) {
    for (const itemId of itemIds) {
      const cardId = cardIdByItemId[itemId];
      // No card means this word has never been scheduled: it counted on screen and has
      // no review row to update.
      if (cardId === undefined) continue;
      grades.push({
        cardId,
        modality,
        correct,
        elapsedMs: elapsed,
        hintUsed: false,
        reviewedAt: now,
      });
    }
  }
  return grades;
}
